import { useCallback, useMemo, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useEditEmbryos } from '@/services/embryo-service';

import { useFreezePdfPrint } from '../../hooks/useFreezePdfPrint';
import type { LiveEmbryoForPrint } from '../../hooks/useFreezePdfPrint/types';
import { RecaneEmbryosForm } from '../recane-embryos-form';
import { EmbryoFormValues } from '../recane-embryos-form/schema';
import { PrintSelectionStep } from './PrintSelectionStep';
import type { RecanePrintLabelType, RecanePrintWizardProps } from './types';
import { buildPrintEmbryos, groupEmbryosByNewCane } from './utils';

const DEFAULT_LABEL_TYPES: RecanePrintLabelType[] = [
  'CaneTabLabel',
  'GobletLabel',
];

export const RecanePrintWizard = ({
  embryos,
  existingCaneCodes,
  onCancel,
  onSuccess,
  onError,
}: RecanePrintWizardProps) => {
  const { t } = useTranslation();
  const stepperRef = useRef<Stepper>(null);
  const printEmbryosRef = useRef<LiveEmbryoForPrint[]>([]);

  const isRecanedIndicatorEnabled = useFeatureFlag(
    'Shipping_Recaned_Indicator',
  );

  const [recaneValues, setRecaneValues] = useState<EmbryoFormValues>();
  const [selectedCanes, setSelectedCanes] = useState<string[]>([]);
  const [labelTypes, setLabelTypes] =
    useState<RecanePrintLabelType[]>(DEFAULT_LABEL_TYPES);

  const { mutateAsync: editEmbryosMutation, isPending: isSubmitting } =
    useEditEmbryos();

  const getEmbryoData = useCallback(
    () => ({
      selectedTempEmbryos: [],
      selectedLiveEmbryos: printEmbryosRef.current,
      fertilizations: [],
    }),
    [],
  );

  const handlePrintComplete = useCallback(() => {}, []);

  const { printPdf, isPrinting } = useFreezePdfPrint({
    getEmbryoData,
    onComplete: handlePrintComplete,
  });

  const groups = useMemo(
    () => groupEmbryosByNewCane(recaneValues),
    [recaneValues],
  );

  const handleRecaneStepComplete = useCallback((values: EmbryoFormValues) => {
    setRecaneValues(values);
    setSelectedCanes(groupEmbryosByNewCane(values).map((group) => group.cane));
    stepperRef.current?.nextCallback?.();
  }, []);

  const handleBack = useCallback(() => {
    stepperRef.current?.prevCallback?.();
  }, []);

  const handleToggleCane = useCallback((cane: string) => {
    setSelectedCanes((current) =>
      current.includes(cane)
        ? current.filter((item) => item !== cane)
        : [...current, cane],
    );
  }, []);

  const handleToggleAllCanes = useCallback(
    (checked: boolean) => {
      setSelectedCanes(checked ? groups.map((group) => group.cane) : []);
    },
    [groups],
  );

  const handleToggleLabelType = useCallback(
    (labelType: RecanePrintLabelType) => {
      setLabelTypes((current) =>
        current.includes(labelType)
          ? current.filter((item) => item !== labelType)
          : [...current, labelType],
      );
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!recaneValues) return;

    try {
      const editInputs = recaneValues.embryos.map((embryo) => ({
        embryoId: embryo.embryoId,
        cane: embryo.newCane,
        ...(isRecanedIndicatorEnabled ? { recaned: true } : {}),
      }));

      const results = await editEmbryosMutation(editInputs);

      const hasErrors = results.some((result) => !result.success);
      if (hasErrors) {
        const errorMessages = results
          .filter((result) => !result.success)
          .map((result) => result.message || t('common.unknownError'))
          .join(', ');
        throw new Error(errorMessages);
      }

      // The cane edits are committed at this point, so report success before
      // printing — a printer failure should not look like a re-cane failure.
      onSuccess?.(recaneValues);

      if (labelTypes.length > 0 && selectedCanes.length > 0) {
        printEmbryosRef.current = buildPrintEmbryos(
          groups,
          selectedCanes,
          embryos ?? [],
        );

        try {
          for (const labelType of labelTypes) {
            await printPdf(labelType);
          }
        } catch (printError) {
          onError?.(printError);
        }
      }
    } catch (error) {
      onError?.(error);
    }
  }, [
    recaneValues,
    isRecanedIndicatorEnabled,
    editEmbryosMutation,
    t,
    labelTypes,
    selectedCanes,
    groups,
    embryos,
    printPdf,
    onSuccess,
    onError,
  ]);

  const isBusy = isSubmitting || isPrinting;

  return (
    <div className="flex h-full flex-col">
      <Stepper ref={stepperRef} className="w-full" data-testid="stepper">
        <StepperPanel header={t('embryo.recaneStep')}>
          <RecaneEmbryosForm
            embryos={embryos}
            existingCaneCodes={existingCaneCodes}
            initialValues={recaneValues}
            submitLabel={t('common.next')}
            onCancel={onCancel}
            onSubmitValues={handleRecaneStepComplete}
          />
        </StepperPanel>
        <StepperPanel header={t('embryo.printStep')}>
          <PrintSelectionStep
            groups={groups}
            selectedCanes={selectedCanes}
            onToggleCane={handleToggleCane}
            onToggleAllCanes={handleToggleAllCanes}
            labelTypes={labelTypes}
            onToggleLabelType={handleToggleLabelType}
          />
          <div className="flex justify-between gap-2 pt-4">
            <Button
              type="button"
              label={t('common.back')}
              severity="secondary"
              icon="pi pi-arrow-left"
              onClick={handleBack}
              disabled={isBusy}
            />
            <Button
              type="button"
              label={t('common.submit')}
              icon="pi pi-check"
              onClick={handleSubmit}
              loading={isBusy}
              disabled={isBusy || !recaneValues}
            />
          </div>
        </StepperPanel>
      </Stepper>
    </div>
  );
};
